import { useState, useCallback, useMemo } from 'react';
import type { Task, TaskFilter, TaskStatus, TaskPriority } from '@/types/tasks';

// Extended Task type with recurrence and linked entities
export interface ExtendedTask extends Task {
  recurrence?: {
    type: 'daily' | 'weekly' | 'monthly' | 'yearly';
    interval: number;
    endDate?: string;
    occurrences?: number;
  };
  linkedContactId?: string;
  linkedContactName?: string;
  linkedDealId?: string;
  linkedDealName?: string;
  assigneeName?: string;
  reminderMinutes?: number;
}

// Mock data for demonstration
const mockTasks: ExtendedTask[] = [
  {
    id: 'task_1',
    title: 'Follow up with Sarah Johnson',
    description: 'Send proposal and pricing details',
    status: 'todo',
    priority: 'high',
    dueDate: new Date(Date.now() + 1000 * 60 * 60 * 2).toISOString(), // 2 hours from now
    assignedTo: 'user1',
    assigneeName: 'Alex Johnson',
    linkedContactId: '1',
    linkedContactName: 'Sarah Johnson',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    reminderMinutes: 30,
  },
  {
    id: 'task_2',
    title: 'Prepare quarterly report',
    description: 'Compile sales data and analytics for Q1',
    status: 'in_progress',
    priority: 'urgent',
    dueDate: new Date(Date.now() + 1000 * 60 * 60 * 24).toISOString(), // Tomorrow
    assignedTo: 'user1',
    assigneeName: 'Alex Johnson',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
    reminderMinutes: 60,
  },
  {
    id: 'task_3',
    title: 'Team meeting with Engineering',
    description: 'Discuss new feature roadmap',
    status: 'todo',
    priority: 'medium',
    dueDate: new Date(Date.now() + 1000 * 60 * 60 * 4).toISOString(),
    assignedTo: 'user2',
    assigneeName: 'Jane Smith',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
  },
  {
    id: 'task_4',
    title: 'Call Michael Chen',
    description: 'Discuss contract renewal terms',
    status: 'overdue',
    priority: 'urgent',
    dueDate: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(), // Yesterday
    assignedTo: 'user1',
    assigneeName: 'Alex Johnson',
    linkedContactId: '2',
    linkedContactName: 'Michael Chen',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 72).toISOString(),
    reminderMinutes: 15,
  },
  {
    id: 'task_5',
    title: 'Update investor records',
    description: 'Clean up duplicate contacts and update records',
    status: 'completed',
    priority: 'low',
    dueDate: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
    assignedTo: 'user3',
    assigneeName: 'Mike Brown',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
    completedAt: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
  },
  {
    id: 'task_6',
    title: 'Weekly sales review',
    description: 'Review pipeline and forecast for next week',
    status: 'todo',
    priority: 'medium',
    dueDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * 3).toISOString(), // 3 days from now
    assignedTo: 'user2',
    assigneeName: 'Jane Smith',
    linkedDealId: 'deal_1',
    linkedDealName: 'Enterprise Package',
    recurrence: {
      type: 'weekly',
      interval: 1,
    },
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
  },
  {
    id: 'task_7',
    title: 'Send invoice to Enterprise Solutions',
    description: 'Monthly retainer invoice',
    status: 'todo',
    priority: 'high',
    dueDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * 5).toISOString(),
    assignedTo: 'user1',
    assigneeName: 'Alex Johnson',
    linkedContactId: '10',
    linkedContactName: 'Mark Thompson',
    linkedDealId: 'deal_2',
    linkedDealName: 'Consulting Services',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
  },
  {
    id: 'task_8',
    title: 'Follow up on proposal',
    description: 'Check with David Park on the proposal sent last week',
    status: 'overdue',
    priority: 'high',
    dueDate: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
    assignedTo: 'user1',
    assigneeName: 'Alex Johnson',
    linkedContactId: '8',
    linkedContactName: 'David Park',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
  },
];

const assigneeOptions = [
  { value: 'user1', label: 'Alex Johnson' },
  { value: 'user2', label: 'Jane Smith' },
  { value: 'user3', label: 'Mike Brown' },
];

export function useTasks() {
  const [tasks, setTasks] = useState<ExtendedTask[]>(mockTasks);
  const [filters, setFilters] = useState<TaskFilter>({});
  const [isLoading, setIsLoading] = useState(false);

  // Filter tasks based on current filters
  const filteredTasks = useMemo(() => {
    let result = [...tasks];
    const now = new Date();
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

    // Apply status filter
    if (filters.status) {
      if (filters.status === 'today') {
        result = result.filter((task) => {
          if (!task.dueDate) return false;
          const due = new Date(task.dueDate);
          return due <= todayEnd && task.status !== 'completed';
        });
      } else if (filters.status === 'upcoming') {
        result = result.filter((task) => {
          if (!task.dueDate) return false;
          const due = new Date(task.dueDate);
          return due > todayEnd && task.status !== 'completed';
        });
      } else if (filters.status === 'overdue') {
        result = result.filter((task) => task.status === 'overdue');
      } else if (filters.status === 'completed') {
        result = result.filter((task) => task.status === 'completed');
      }
    }

    // Apply priority filter
    if (filters.priority) {
      result = result.filter((task) => task.priority === filters.priority);
    }

    // Apply assignee filter
    if (filters.assignedTo) {
      result = result.filter((task) => task.assignedTo === filters.assignedTo);
    }

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(
        (task) =>
          task.title.toLowerCase().includes(searchLower) ||
          task.description?.toLowerCase().includes(searchLower) ||
          task.linkedContactName?.toLowerCase().includes(searchLower) ||
          task.linkedDealName?.toLowerCase().includes(searchLower)
      );
    }

    // Apply linked entity filters
    if (filters.linkedContactId) {
      result = result.filter((task) => task.linkedContactId === filters.linkedContactId);
    }
    if (filters.linkedDealId) {
      result = result.filter((task) => task.linkedDealId === filters.linkedDealId);
    }

    // Sort by due date (overdue first, then by due date)
    result.sort((a, b) => {
      // Completed tasks go to the bottom
      if (a.status === 'completed' && b.status !== 'completed') return 1;
      if (b.status === 'completed' && a.status !== 'completed') return -1;

      // Overdue tasks go to the top
      if (a.status === 'overdue' && b.status !== 'overdue') return -1;
      if (b.status === 'overdue' && a.status !== 'overdue') return 1;

      // Then sort by due date
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    });

    return result;
  }, [tasks, filters]);

  // Stats
  const stats = useMemo(() => {
    const now = new Date();
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

    let total = tasks.length;
    let completed = 0;
    let overdue = 0;
    let today = 0;
    let upcoming = 0;

    tasks.forEach((task) => {
      if (task.status === 'completed') {
        completed++;
      } else if (task.status === 'overdue') {
        overdue++;
      } else if (task.dueDate) {
        const due = new Date(task.dueDate);
        if (due <= todayEnd) {
          today++;
        } else {
          upcoming++;
        }
      }
    });

    return {
      total,
      completed,
      overdue,
      today,
      upcoming,
      completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [tasks]);

  // Actions
  const addTask = useCallback((taskData: Omit<ExtendedTask, 'id' | 'createdAt'>) => {
    const newTask: ExtendedTask = {
      ...taskData,
      id: `task_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
    };
    setTasks((prev) => [newTask, ...prev]);
    return newTask;
  }, []);

  const updateTask = useCallback((taskId: string, updates: Partial<ExtendedTask>) => {
    setTasks((prev) =>
      prev.map((task) =>
        task.id === taskId
          ? { ...task, ...updates }
          : task
      )
    );
  }, []);

  const deleteTask = useCallback((taskId: string) => {
    setTasks((prev) => prev.filter((task) => task.id !== taskId));
  }, []);

  const completeTask = useCallback((taskId: string) => {
    setTasks((prev) =>
      prev.map((task) =>
        task.id === taskId
          ? { ...task, status: 'completed' as TaskStatus, completedAt: new Date().toISOString() }
          : task
      )
    );
  }, []);

  const reopenTask = useCallback((taskId: string) => {
    setTasks((prev) =>
      prev.map((task) =>
        task.id === taskId
          ? { ...task, status: 'todo' as TaskStatus, completedAt: undefined }
          : task
      )
    );
  }, []);

  const assignTask = useCallback((taskId: string, assigneeId: string, assigneeName: string) => {
    setTasks((prev) =>
      prev.map((task) =>
        task.id === taskId
          ? { ...task, assignedTo: assigneeId, assigneeName }
          : task
      )
    );
  }, []);

  // Check and update overdue tasks
  const checkOverdue = useCallback(() => {
    const now = new Date();
    setTasks((prev) =>
      prev.map((task) => {
        if (task.status !== 'completed' && task.dueDate) {
          const due = new Date(task.dueDate);
          if (due < now) {
            return { ...task, status: 'overdue' as TaskStatus };
          }
        }
        return task;
      })
    );
  }, []);

  // Get tasks by date for calendar view
  const getTasksByDate = useCallback((date: Date) => {
    const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dateEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);

    return tasks.filter((task) => {
      if (!task.dueDate) return false;
      const due = new Date(task.dueDate);
      return due >= dateStart && due <= dateEnd;
    });
  }, [tasks]);

  // Get upcoming tasks for notifications
  const getUpcomingTasks = useCallback((minutesAhead: number = 30) => {
    const now = new Date();
    const future = new Date(now.getTime() + minutesAhead * 60000);

    return tasks.filter((task) => {
      if (task.status === 'completed' || !task.dueDate) return false;
      const due = new Date(task.dueDate);
      return due >= now && due <= future;
    });
  }, [tasks]);

  return {
    tasks,
    filteredTasks,
    stats,
    filters,
    setFilters,
    isLoading,
    addTask,
    updateTask,
    deleteTask,
    completeTask,
    reopenTask,
    assignTask,
    checkOverdue,
    getTasksByDate,
    getUpcomingTasks,
    assigneeOptions,
  };
}

export default useTasks;
